import React, { useState, useEffect, useRef } from 'react';
import { RotateCcw, Volume2, VolumeX, CheckCircle, ChevronRight, Star, TrendingUp, Loader2, Layers, ListChecks, Eye, Activity, Lightbulb, HelpCircle, Sparkles, Lock, ArrowDown } from 'lucide-react';

const SECTION_META = {
    identificacion:  { icon: Layers,     variant: 'primary' },
    criterios:       { icon: ListChecks, variant: 'primary' },
    comportamientos: { icon: Eye,        variant: 'primary' },
    efecto:          { icon: Activity,   variant: 'primary' },
    alternativas:    { icon: Lightbulb,  variant: 'primary' },
    reflexion:       { icon: HelpCircle, variant: 'reflexion' },
    cierre:          { icon: Sparkles,   variant: 'cierre' },
};

// El backend devuelve algunos `header` poco explícitos (p. ej. "ALTERNATIVAS").
// Mapeamos por id a una clave de i18n para mostrar un título más claro al usuario.
const SECTION_HEADER_OVERRIDES = {
    alternativas: 'feedbackHeaderAlternativas',
};
import BackgroundStack from './background/BackgroundStack';
import { useI18n } from '../i18n';
import LanguageSelector from './LanguageSelector';
import FeedbackSurvey from './FeedbackSurvey';

// ─── JSON schema (lo que el backend debe devolver) ─────────────────────────────
//
// {
//   "greeting": "¡Gracias por participar en esta simulación!",   // opcional
//   "sections": [
//     {
//       "id": "identificacion",          // opcional, para debugging
//       "header": "IDENTIFICACIÓN DE LA HABILIDAD",
//       "text": "La habilidad trabajada fue **comunicación**...", // inline **bold** permitido
//       "bullets": ["**Parafraseo.** En varios momentos..."],     // opcional
//       "subitems": [                                             // opcional
//         { "title": "PARAFRASEAR EN UNA FRASE BREVE.", "text": "Esto ayuda a..." }
//       ]
//     }
//   ]
// }
//
// Todos los campos de texto admiten **negrita** inline.
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Extrae el primer objeto JSON bien formado de un string que puede contener
 * texto adicional antes, después o repeticiones del mismo JSON.
 */
function extractFirstJson(text) {
    const start = text.indexOf('{');
    if (start === -1) return null;
    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let i = start; i < text.length; i++) {
        const ch = text[i];
        if (escaped) { escaped = false; continue; }
        if (ch === '\\' && inString) { escaped = true; continue; }
        if (ch === '"') { inString = !inString; continue; }
        if (inString) continue;
        if (ch === '{') depth++;
        else if (ch === '}') {
            depth--;
            if (depth === 0) return text.slice(start, i + 1);
        }
    }
    return null;
}

/**
 * Intenta parsear feedbackMessage como JSON con la estructura esperada.
 * Maneja: JSON directo, JSON con code fences (```json```), JSON envuelto
 * en comillas externas, o JSON duplicado/concatenado (bug del backend).
 * Si nada funciona aplica el parser de fallback que convierte markdown.
 */
function parseFeedbackData(raw) {
    if (!raw || !raw.trim()) return null;

    let clean = raw.trim();

    // 1. Quitar code fences (```json ... ```)
    const fenceMatch = clean.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
    if (fenceMatch) clean = fenceMatch[1].trim();

    // 2. Quitar comillas externas si el backend las añade ("{ ... }")
    if (clean.startsWith('"') && clean.endsWith('"')) {
        try { clean = JSON.parse(clean); } catch (_) {
            clean = clean.slice(1, -1);
        }
    }

    // 3. Intentar JSON directo
    const tryParse = (str) => {
        try {
            const parsed = JSON.parse(str);
            if (parsed && Array.isArray(parsed.sections) && parsed.sections.length > 0) {
                return {
                    greeting: parsed.greeting || null,
                    sections: parsed.sections.map(s => ({
                        id: s.id || null,
                        header: s.header || null,
                        text: s.text || '',
                        bullets: Array.isArray(s.bullets) ? s.bullets : [],
                        subitems: Array.isArray(s.subitems)
                            ? s.subitems.map(si => ({ title: si.title || '', text: si.text || '' }))
                            : [],
                    })),
                };
            }
        } catch (_) {}
        return null;
    };

    const direct = tryParse(clean);
    if (direct) return direct;

    // 4. Extraer el primer JSON válido del string (cubre JSON duplicado/concatenado)
    const firstJson = extractFirstJson(clean);
    if (firstJson) {
        const fromFirst = tryParse(firstJson);
        if (fromFirst) return fromFirst;
    }

    // 5. Fallback: parsear markdown/texto plano al mismo schema
    return markdownToFeedbackData(clean);
}

/**
 * Convierte texto markdown (formato legacy del backend) al schema JSON.
 * Heurística:
 *   - Línea completamente en **bold** sin puntuación final → encabezado de sección
 *   - Línea completamente en **bold** con . / ? / ! al final → sub-item dentro de la sección
 *   - Línea que empieza con • o - → bullet de la sección actual
 *   - Texto libre → `text` de la sección o `text` del último sub-item
 */
function markdownToFeedbackData(text) {
    const sections = [];
    let current = null;
    let greetingLines = [];
    let foundFirstSection = false;

    for (const line of text.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        const isBullet = /^[-•]\s/.test(trimmed);
        const boldMatch = trimmed.match(/^\*\*(.+?)\*\*\s*$/);
        const isMarkdownHeading = /^(##|###|#)/.test(trimmed);
        const isNumbered = /^\d+\.\s/.test(trimmed);
        const isHeader = boldMatch || isMarkdownHeading || isNumbered;

        if (isHeader) {
            foundFirstSection = true;
            const rawHeader = boldMatch
                ? boldMatch[1]
                : trimmed.replace(/^(##|###|#|\d+\.\s)/, '').trim();
            const endsWithPunctuation = /[.?!]$/.test(rawHeader);

            if (endsWithPunctuation && current) {
                current.subitems.push({ title: rawHeader, text: '' });
            } else {
                if (current) sections.push(current);
                current = { id: null, header: rawHeader, text: '', bullets: [], subitems: [] };
            }
        } else if (isBullet) {
            const bulletText = trimmed.replace(/^[-•]\s/, '').trim();
            if (current) {
                current.bullets.push(bulletText);
            } else {
                sections.push({ id: null, header: null, text: '', bullets: [bulletText], subitems: [] });
            }
        } else {
            if (!foundFirstSection) {
                greetingLines.push(trimmed);
            } else if (current && current.subitems.length > 0) {
                const last = current.subitems[current.subitems.length - 1];
                last.text += (last.text ? ' ' : '') + trimmed;
            } else if (current) {
                current.text += (current.text ? ' ' : '') + trimmed;
            } else {
                sections.push({ id: null, header: null, text: trimmed, bullets: [], subitems: [] });
            }
        }
    }
    if (current) sections.push(current);

    if (sections.length === 0) return null;

    return {
        greeting: greetingLines.length > 0 ? greetingLines.join(' ') : null,
        sections,
    };
}

// ─── Bullets/subitems compactos para "criterios" ─────────────────────────────
// Mantienen la sección densa: si el texto excede ~140 chars (bullet) o ~180
// (subitem) se muestra recortado con un toggle "ver más / ver menos".

const BULLET_LIMIT = 140;
const SUBITEM_LIMIT = 180;

function CompactBullet({ text, sizeClass, renderInline, collapsible }) {
    const [open, setOpen] = useState(false);
    const long = collapsible && (text?.length || 0) > BULLET_LIMIT;
    const shown = !long || open ? text : text.slice(0, BULLET_LIMIT).trimEnd() + '…';
    return (
        <li className={`flex items-start gap-2 ${sizeClass} text-stone-600 leading-snug`}>
            <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: '#1A4731' }} />
            <span className="flex-1">
                {renderInline(shown)}
                {long && (
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className="ml-1.5 text-[11px] font-medium underline decoration-dotted"
                        style={{ color: '#1A4731' }}
                    >
                        {open ? 'ver menos' : 'ver más'}
                    </button>
                )}
            </span>
        </li>
    );
}

function CompactSubitem({ text, renderInline }) {
    const [open, setOpen] = useState(false);
    const long = (text?.length || 0) > SUBITEM_LIMIT;
    const shown = !long || open ? text : text.slice(0, SUBITEM_LIMIT).trimEnd() + '…';
    return (
        <p className="text-[11px] text-stone-500 leading-snug">
            {renderInline(shown)}
            {long && (
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="ml-1.5 text-[11px] font-medium underline decoration-dotted"
                    style={{ color: '#1A4731' }}
                >
                    {open ? 'ver menos' : 'ver más'}
                </button>
            )}
        </p>
    );
}

// ─── Componente ───────────────────────────────────────────────────────────────

function FeedbackView({
    feedbackMessage,
    scenarioInfo,
    selectedSkills,
    skillNames,
    progress,
    onRestart,
    isTtsPlaying,
    isTtsLoading,
    onPlayTTS,
    onStopTTS,
    selectedLanguage,
    onLanguageChange,
    threadId,
    selectedLevel,
}) {
    const { t, lang } = useI18n();
    const [appeared, setAppeared] = useState(false);
    const surveyRef = useRef(null);
    const surveyHasBeenSeenRef = useRef(false);
    const [surveyVisible, setSurveyVisible] = useState(false);
    const [surveyHighlight, setSurveyHighlight] = useState(false);

    const SUBMITTED_PREFIX = 'survey_submitted:';
    const [surveyDone, setSurveyDone] = useState(() => {
        if (!threadId) return true;
        try { return window.localStorage.getItem(`${SUBMITTED_PREFIX}${threadId}`) === '1'; } catch { return false; }
    });

    useEffect(() => {
        const timer = setTimeout(() => setAppeared(true), 80);
        return () => clearTimeout(timer);
    }, []);

    const isReady = feedbackMessage && feedbackMessage.trim() !== '';
    const feedbackData = isReady ? parseFeedbackData(feedbackMessage) : null;

    // Detecta cuando la encuesta entra al viewport (porque el usuario hizo scroll
    // hasta el final del feedback). Apaga la pill y dispara un highlight breve la
    // primera vez que aparece, para confirmar visualmente "llegaste".
    useEffect(() => {
        if (!isReady || !threadId || surveyDone) return;
        const node = surveyRef.current;
        if (!node || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                const visible = entry.isIntersecting && entry.intersectionRatio > 0.25;
                setSurveyVisible(visible);
                if (visible && !surveyHasBeenSeenRef.current) {
                    surveyHasBeenSeenRef.current = true;
                    setSurveyHighlight(true);
                    setTimeout(() => setSurveyHighlight(false), 1600);
                }
            },
            { threshold: [0, 0.25, 0.5] },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [isReady, threadId, surveyDone]);

    const restartBlocked = !!threadId && !surveyDone;
    const handleRestartAttempt = () => {
        if (restartBlocked) {
            surveyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        }
        onRestart?.();
    };

    const handleJumpToSurvey = () => {
        surveyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const showPendingPill = isReady && !!threadId && !surveyDone && !surveyVisible;

    // Separa secciones "primary" (van en grid 2 cols) de reflexion/cierre
    // (full-width abajo, son conversacionales y suelen ser cortas).
    const primarySections = (feedbackData?.sections || []).filter(s => {
        const v = SECTION_META[s.id]?.variant || 'primary';
        return v === 'primary';
    });
    const trailingSections = (feedbackData?.sections || []).filter(s => {
        const v = SECTION_META[s.id]?.variant || 'primary';
        return v !== 'primary';
    });

    const progressPct = progress != null
        ? Math.round(Math.max(0, Math.min(1, progress)) * 100)
        : null;

    // Renderiza inline markdown: **bold** → <strong>
    const renderInline = (text) => {
        if (!text) return null;
        return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
            part.startsWith('**') && part.endsWith('**')
                ? <strong key={i} className="font-semibold text-stone-800">{part.slice(2, -2)}</strong>
                : part
        );
    };

    return (
        <BackgroundStack
            className={`fixed inset-0 z-50 min-h-0 w-full overflow-y-auto transition-opacity duration-500 ${appeared ? 'opacity-100' : 'opacity-0'}`}
            sidebarExpanded={false}
        >
            {/* Header */}
            <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#1A4731' }}>
                            <span className="text-white font-bold text-sm">S</span>
                        </div>
                        <div>
                            <span className="font-semibold text-stone-800 text-sm">SimAI</span>
                            <span className="text-stone-400 text-sm mx-2">|</span>
                            <span className="text-stone-500 text-sm">{t('simulationResult')}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {selectedLanguage && onLanguageChange && (
                            <LanguageSelector selectedLanguage={selectedLanguage} onLanguageChange={onLanguageChange} />
                        )}
                        <button
                            onClick={handleRestartAttempt}
                            title={restartBlocked ? t('surveyMustComplete') : ''}
                            className={`flex items-center gap-1.5 text-sm transition-colors px-3 py-1.5 rounded-lg ${restartBlocked ? 'text-stone-400 cursor-not-allowed' : 'text-stone-500 hover:text-stone-800 hover:bg-stone-100'}`}
                        >
                            {restartBlocked ? <Lock className="w-4 h-4" /> : <RotateCcw className="w-4 h-4" />}
                            {t('newSimulation')}
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 py-6 pb-24">

                {/* Hero compacto: título + contexto en una sola fila + TTS */}
                <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Star className="w-5 h-5 shrink-0" style={{ color: '#1A4731' }} />
                        <div className="min-w-0">
                            <h2 className="text-xl font-bold text-stone-900 leading-tight">{t('yourFeedback')}</h2>
                            {(scenarioInfo?.nombre || (skillNames && skillNames.length > 0)) && (
                                <p className="text-xs text-stone-500 mt-0.5 truncate">
                                    {[scenarioInfo?.nombre, skillNames?.join(', ')].filter(Boolean).join(' · ')}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        {progressPct != null && (
                            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ background: '#E8F5EE' }}>
                                <span className="text-xs uppercase tracking-wider text-stone-500">{t('progress')}</span>
                                <span className="text-base font-bold" style={{ color: '#1A4731' }}>{progressPct}%</span>
                            </div>
                        )}
                        {isTtsLoading ? (
                            <div className="flex items-center gap-1.5 text-xs text-stone-400">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>{t('preparingAudio')}</span>
                            </div>
                        ) : isTtsPlaying ? (
                            <button
                                onClick={onStopTTS}
                                className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 px-3 py-1.5 rounded-lg hover:bg-stone-100 border border-stone-200 transition-all"
                            >
                                <VolumeX className="w-4 h-4" />
                                {t('stopAudio')}
                            </button>
                        ) : isReady && onPlayTTS ? (
                            <button
                                onClick={() => onPlayTTS(feedbackMessage)}
                                className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 px-3 py-1.5 rounded-lg hover:bg-stone-100 border border-stone-200 transition-all"
                            >
                                <Volume2 className="w-4 h-4" />
                                {t('listen')}
                            </button>
                        ) : null}
                    </div>
                </div>

                {/* Feedback content */}
                {!isReady ? (
                    <div className="bg-white rounded-2xl border border-stone-200 p-8 flex flex-col items-center gap-4">
                        <div className="w-12 h-12 rounded-full border-2 border-stone-200 border-t-stone-600 animate-spin" />
                        <p className="text-sm text-stone-500">{t('generatingFeedback')}</p>
                    </div>
                ) : feedbackData ? (
                    <div className="space-y-4">
                        {/* Saludo destacado: tipografía mayor, sin card chrome para no robar foco */}
                        {feedbackData.greeting && (
                            <div className="pb-1">
                                <p className="text-base text-stone-700 leading-relaxed">
                                    {renderInline(feedbackData.greeting)}
                                </p>
                            </div>
                        )}

                        {/* Secciones primary en layout tipo masonry (CSS columns):
                            cada card mide según su contenido y se acomoda en columnas
                            balanceadas. break-inside-avoid evita que se parta entre columnas. */}
                        {primarySections.length > 0 && (
                            <div className="md:columns-2 md:gap-3">
                                {primarySections.map((section, i) => {
                                    const meta = SECTION_META[section.id] || { icon: TrendingUp, variant: 'primary' };
                                    const Icon = meta.icon;
                                    const isCriterios = section.id === 'criterios';
                                    const headerOverrideKey = SECTION_HEADER_OVERRIDES[section.id];
                                    const displayHeader = headerOverrideKey ? t(headerOverrideKey) : section.header;
                                    const bulletText = isCriterios ? 'text-xs' : 'text-sm';
                                    const bulletGap = isCriterios ? 'space-y-1' : 'space-y-1.5';
                                    const subitemGap = isCriterios ? 'space-y-1.5' : 'space-y-2';
                                    return (
                                        <div
                                            key={section.id || `p-${i}`}
                                            className="rounded-2xl border border-stone-200 bg-white p-4 mb-3 break-inside-avoid"
                                        >
                                            {displayHeader && (
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: '#E8F5EE' }}>
                                                        <Icon className="w-3.5 h-3.5" style={{ color: '#1A4731' }} />
                                                    </div>
                                                    <h3 className="font-semibold text-stone-800 text-[11px] uppercase tracking-wide flex-1">
                                                        {displayHeader}
                                                    </h3>
                                                </div>
                                            )}

                                            {section.text && (
                                                <p className={`${isCriterios ? 'text-xs' : 'text-sm'} leading-relaxed text-stone-600`}>
                                                    {renderInline(section.text)}
                                                </p>
                                            )}

                                            {section.bullets?.length > 0 && (
                                                <ul className={`${bulletGap} ${section.text ? 'mt-2' : ''}`}>
                                                    {section.bullets.map((b, j) => (
                                                        <CompactBullet
                                                            key={j}
                                                            text={b}
                                                            sizeClass={bulletText}
                                                            renderInline={renderInline}
                                                            collapsible={isCriterios}
                                                        />
                                                    ))}
                                                </ul>
                                            )}

                                            {section.subitems?.length > 0 && (
                                                <div className={`${subitemGap} ${section.text || section.bullets?.length > 0 ? (isCriterios ? 'mt-2' : 'mt-3') : ''}`}>
                                                    {section.subitems.map((sub, j) => (
                                                        <div key={j} className="border-l-2 pl-3" style={{ borderColor: '#1A4731' }}>
                                                            <p className="text-[11px] font-semibold text-stone-800 mb-0.5 leading-snug">{sub.title}</p>
                                                            {sub.text && (
                                                                isCriterios ? (
                                                                    <CompactSubitem text={sub.text} renderInline={renderInline} />
                                                                ) : (
                                                                    <p className="text-xs text-stone-500 leading-relaxed">
                                                                        {renderInline(sub.text)}
                                                                    </p>
                                                                )
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Reflexión + cierre: full-width al final, son conversacionales */}
                        {trailingSections.length > 0 && (
                            <div className="space-y-3">
                                {trailingSections.map((section, i) => {
                                    const meta = SECTION_META[section.id] || { icon: TrendingUp, variant: 'primary' };
                                    const Icon = meta.icon;
                                    const isReflexion = meta.variant === 'reflexion';
                                    const isCierre = meta.variant === 'cierre';
                                    const cardClass = isCierre ? 'border-stone-200 bg-stone-50' : 'border-stone-200 bg-white';
                                    const iconBoxStyle = isCierre ? { background: '#F5F5F4' } : { background: '#E8F5EE' };
                                    const headerOverrideKey = SECTION_HEADER_OVERRIDES[section.id];
                                    const displayHeader = headerOverrideKey ? t(headerOverrideKey) : section.header;
                                    return (
                                        <div key={section.id || `t-${i}`} className={`rounded-2xl border p-4 ${cardClass}`}>
                                            {displayHeader && (
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={iconBoxStyle}>
                                                        <Icon className="w-3.5 h-3.5" style={{ color: isCierre ? '#78716C' : '#1A4731' }} />
                                                    </div>
                                                    <h3 className="font-semibold text-stone-800 text-[11px] uppercase tracking-wide flex-1">
                                                        {displayHeader}
                                                    </h3>
                                                </div>
                                            )}
                                            {section.text && (
                                                <p className={`text-sm leading-relaxed ${isCierre ? 'text-stone-500 italic' : 'text-stone-600'}`}>
                                                    {renderInline(section.text)}
                                                </p>
                                            )}
                                            {section.bullets?.length > 0 && (
                                                <ul className={`space-y-2 ${section.text ? 'mt-2.5' : ''}`}>
                                                    {section.bullets.map((b, j) => (
                                                        <li key={j} className="flex items-start gap-2 text-sm text-stone-600">
                                                            {isReflexion ? (
                                                                <span className="text-stone-300 font-bold mt-px shrink-0 text-base leading-none">›</span>
                                                            ) : (
                                                                <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: '#1A4731' }} />
                                                            )}
                                                            <span className={`flex-1 ${isReflexion ? 'text-stone-500' : ''}`}>
                                                                {renderInline(b)}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ) : (
                    // Último recurso: texto plano sin estructura
                    <div className="bg-white rounded-2xl border border-stone-200 p-6">
                        <p className="text-sm text-stone-600 leading-relaxed whitespace-pre-wrap">
                            {renderInline(feedbackMessage)}
                        </p>
                    </div>
                )}

                {isReady && threadId && (
                    <FeedbackSurvey
                        ref={surveyRef}
                        threadId={threadId}
                        locale={lang}
                        highlight={surveyHighlight}
                        onSubmitted={() => setSurveyDone(true)}
                        metadata={{
                            scenario_slug: scenarioInfo?.slug || scenarioInfo?.id || null,
                            scenario_nombre: scenarioInfo?.nombre || null,
                            tipo_proyecto: scenarioInfo?.tipo_proyecto || null,
                            habilidades: selectedSkills || [],
                            habilidad_nombres: skillNames || [],
                            nivel: selectedLevel ?? null,
                            progress: progress ?? null,
                        }}
                    />
                )}
            </div>

            {/* Pill flotante: encuesta pendiente */}
            {showPendingPill && (
                <div className="fixed right-5 bottom-24 z-20">
                    {/* Halo difuso pulsante */}
                    <span
                        className="absolute inset-0 rounded-full animate-ping"
                        style={{ background: 'rgba(34, 197, 94, 0.35)', animationDuration: '1.8s' }}
                    />
                    <button
                        type="button"
                        onClick={handleJumpToSurvey}
                        className="survey-pending-pill relative flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white transition-transform hover:scale-[1.05]"
                        style={{
                            background: 'linear-gradient(135deg, #16A34A 0%, #1A4731 100%)',
                            boxShadow: '0 8px 24px rgba(34, 197, 94, 0.45), 0 0 0 1px rgba(255,255,255,0.08) inset',
                        }}
                        aria-label={t('surveyJumpToSurvey')}
                    >
                        <span className="relative flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-300 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400" />
                        </span>
                        {t('surveyPendingPill')}
                        <ArrowDown className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Bottom CTA */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
                    <p className="text-sm text-stone-500">
                        {restartBlocked ? t('surveyMustComplete') : t('readyAgain')}
                    </p>
                    <button
                        onClick={handleRestartAttempt}
                        title={restartBlocked ? t('surveyMustComplete') : ''}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all shrink-0 ${restartBlocked ? 'bg-stone-200 text-stone-500 cursor-not-allowed' : 'text-white hover:opacity-90 hover:scale-[1.02]'}`}
                        style={restartBlocked ? {} : { background: '#1A4731' }}
                    >
                        {restartBlocked && <Lock className="w-4 h-4" />}
                        {t('newSimulation')}
                        {!restartBlocked && <ChevronRight className="w-4 h-4" />}
                    </button>
                </div>
            </div>
        </BackgroundStack>
    );
}

export default FeedbackView;
